import { useEffect, useRef, useState } from "react";
import { useData } from "../context/DataContext";
import { toast } from "sonner";
import { Pencil, Check, X, NotebookPen } from "lucide-react";
import InfoTip from "./InfoTip";

// Editable, persisted note attached to the dataset's meta.notes.
// Lives on the Overview — a quiet place to drop in context for this period:
// hiring decisions, fee proposals in flight, market commentary, anything the
// firm wants to read alongside the numbers next time they open the dashboard.

const fmtStamp = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const day = d.toLocaleDateString("en-NZ", { day: "2-digit", month: "short" });
  const time = d.toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit" });
  return `${day} · ${time}`;
};

export default function ReportNotes() {
  const { rawData, updateReportNotes } = useData();
  const meta = rawData?.meta || {};
  const saved = meta.notes || "";
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(saved);
  const [busy, setBusy] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => { setDraft(saved); }, [saved]);

  const startEdit = () => {
    setDraft(saved);
    setEditing(true);
    requestAnimationFrame(() => textareaRef.current?.focus());
  };

  const cancel = () => { setDraft(saved); setEditing(false); };

  const save = async () => {
    setBusy(true);
    try {
      await updateReportNotes(draft.trim());
      toast.success("Forecast notes saved");
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save notes");
    } finally {
      setBusy(false);
    }
  };

  const isEmpty = !saved && !editing;

  return (
    <section
      className="border border-[var(--rule)] p-5 bg-[var(--paper-soft)]"
      data-testid="report-notes"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <NotebookPen size={14} className="text-[var(--muted-fg)]" />
          <div className="eyebrow inline-flex items-center">
            Forecast notes
            <InfoTip side="bottom" label="What 'Forecast notes' is for">
              Free-form commentary that lives with this dataset. Drop in context for next time you open the dashboard — fee proposals in flight, hiring decisions, market notes, anything that explains the numbers.
            </InfoTip>
          </div>
          {meta.notesUpdatedAt && (
            <span className="eyebrow text-[var(--soft-fg)] normal-case tracking-[0.06em]" data-testid="notes-stamp">
              · updated {fmtStamp(meta.notesUpdatedAt)}
              {meta.notesUpdatedBy ? ` · ${meta.notesUpdatedBy}` : ""}
            </span>
          )}
        </div>
        {!editing && (
          <button
            onClick={startEdit}
            className="inline-flex items-center gap-2 px-3 py-1.5 border border-[var(--rule-strong)] text-[11px] font-display tracked-tight uppercase hover:bg-white"
            data-testid="notes-edit"
          >
            <Pencil size={11} /> {saved ? "Edit" : "Add note"}
          </button>
        )}
      </div>

      {editing ? (
        <div className="mt-4">
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Drop in context for this forecast period — proposals out, hiring conversations, decisions made, market signals…"
            className="w-full min-h-[140px] bg-white p-3 border border-[var(--rule)] outline-none focus:border-[var(--ink)] font-mono-num text-[12px] leading-relaxed"
            data-testid="notes-textarea"
            spellCheck
          />
          <div className="flex justify-end gap-2 mt-3">
            <button
              onClick={cancel}
              className="inline-flex items-center gap-2 px-3 py-2 text-xs font-display tracked-tight uppercase border border-[var(--rule)] hover:bg-white"
              data-testid="notes-cancel"
            >
              <X size={12} /> Cancel
            </button>
            <button
              onClick={save}
              disabled={busy}
              className="inline-flex items-center gap-2 px-3 py-2 text-xs font-display tracked-tight uppercase bg-[var(--ink)] text-white disabled:opacity-40"
              data-testid="notes-save"
            >
              <Check size={12} /> {busy ? "Saving…" : "Save note"}
            </button>
          </div>
        </div>
      ) : isEmpty ? (
        <div className="mt-3 text-[12px] font-mono-num text-[var(--soft-fg)] leading-relaxed">
          Nothing yet. Click <strong className="text-[var(--ink)]">Add note</strong> to drop in commentary for this forecast period.
        </div>
      ) : (
        <div
          className="mt-3 font-mono-num text-[12px] leading-relaxed text-[var(--ink)] whitespace-pre-wrap"
          data-testid="notes-display"
        >
          {saved}
        </div>
      )}
    </section>
  );
}
